import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { deserializer } from '@shared/helpers/deserializer.helper';
import { errorHandler } from '@shared/helpers/handle-error.helper';
import { IItem } from '@shared/interfaces/item.interface';
import { IPaginable } from '@shared/interfaces/paginable.interface';
import { Item } from '@shared/models/item.model';
import { ItemsSerializer } from '@shared/serializers/items.serializer';
import { CommonEnvironmentsService } from '@shared/services/environments.service';
import { Observable } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ItemsService implements IPaginable {
  private readonly endpoint: string = String(
    `${this._commonEnvironments.API_ENDPOINT}/items`
  );

  constructor(
    private readonly _commonEnvironments: CommonEnvironmentsService,
    private readonly _http: HttpClient
  ) {}

  public delete(id: string): Observable<IItem> {
    return this._http.delete(`${this.endpoint}/${id}`).pipe(
      map((res: any) => new Item(deserializer.deserialize(res))),
      catchError(errorHandler)
    );
  }

  public find(id: string): Observable<IItem> {
    return this._http.get(`${this.endpoint}/${id}`).pipe(
      map((res: any) => new Item(deserializer.deserialize(res))),
      catchError(errorHandler)
    );
  }

  public get(
    pageNumber?: number,
    filters?: any,
    onGetRawData?: (data: any) => void
  ): Observable<IItem[]> {
    let httpParams = new HttpParams();

    if (pageNumber) {
      httpParams = httpParams.set('page[number]', String(pageNumber));
    }

    if (filters) {
      filters.map((f) => {
        if (f.value) {
          httpParams = httpParams.append(f.key, f.value);
        }
      });
    }

    return this._http.get(this.endpoint, { params: httpParams }).pipe(
      tap((data: any) => onGetRawData && onGetRawData(data)),
      map((res: any) =>
        deserializer.deserialize(res).map((i: IItem) => new Item(i))
      ),
      catchError(errorHandler)
    );
  }

  public patch(i: Partial<IItem>): Observable<IItem> {
    const data = ItemsSerializer.serialize(i);
    return this._http.patch(`${this.endpoint}/${i.id}`, data).pipe(
      map((res: any) => new Item(deserializer.deserialize(res))),
      catchError(errorHandler)
    );
  }

  public post(i: IItem): Observable<IItem> {
    const data = ItemsSerializer.serialize(i);
    return this._http.post(this.endpoint, data).pipe(
      map((res: any) => new Item(deserializer.deserialize(res))),
      catchError(errorHandler)
    );
  }
}
